import { useHubStore } from "@/components/hub/use-hub-store";
import { ACTIVITY_REGISTRY } from "@/lib/activities/registry";
import { useShareableEntity } from "@/lib/api/shareable-entities";
import { parseMessageData } from "@/lib/utils/parse-message-data";
import { useCallback, useMemo } from "react";

export interface EntityInteractionData {
  interaction_type: "comment" | "reaction";
  entity_type: "note" | "moment" | "swap" | "activity";
  entity_id: string;
  activity_type?: string;
  preview_url?: string | null;
  second_preview_url?: string | null;
  note_preview?: string | null;
  metadata?: {
    index?: number;
  };
}

export function useEntityInteraction(message: { data?: string }) {
  const entityData = useMemo(
    () => parseMessageData<EntityInteractionData>(message.data),
    [message.data],
  );

  const isActivityInteraction = entityData?.entity_type === "activity";
  const shareableEntityId = entityData?.entity_id ?? "";
  const { data: entity, isError: isUnavailable } =
    useShareableEntity(shareableEntityId);

  const openToView = useHubStore((state) => state.openToView);

  const activityConfig =
    entityData?.entity_type === "activity" && entityData.activity_type
      ? ACTIVITY_REGISTRY[entityData.activity_type]
      : undefined;
  const index = entityData?.metadata?.index ?? 0;

  // Interaction messages already exist in chat; render thumbnails for any
  // registered activity type regardless of rollout feature flags.
  const isEnabled =
    !isActivityInteraction || Boolean(activityConfig);

  const openInHub = useCallback(() => {
    if (!entityData || !shareableEntityId || isUnavailable) return;
    if (activityConfig) {
      openToView(activityConfig.hubView, shareableEntityId, index);
      return;
    }
    const viewMap = {
      moment: "view-moment",
      note: "view-note",
      swap: "view-swap",
    } as const;
    const target = viewMap[entityData.entity_type as keyof typeof viewMap];
    if (target) openToView(target, shareableEntityId);
  }, [
    entityData,
    shareableEntityId,
    isUnavailable,
    activityConfig,
    index,
    openToView,
  ]);

  return {
    entityData,
    entity,
    isUnavailable,
    activityConfig,
    isEnabled,
    index,
    openInHub,
  };
}
